import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { Title, ErrorState, Skeleton } from "@common/index";
import { useDashboardApi } from "@/hooks/apiHooks/useDashboardApi";
import { useMountRead } from "@/hooks/useMountRead";
import { dashboardAtom } from "@/store/dashboardAtoms";
import { KpiTile } from "./_components/KpiTile";
import { IssuesTooltip } from "./_components/IssuesTooltip";

const REFRESH_INTERVAL_MS = 60_000;

const GRID_CLASSES =
    "mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4";

export const Dashboard = () => {
    const { getDashboard } = useDashboardApi();
    const dashboard = useAtomValue(dashboardAtom);
    // Issued on mount; the page shows a failure only for its own read (#407).
    const { readStatus, reload } = useMountRead(getDashboard);

    useEffect(() => {
        const intervalId = setInterval(reload, REFRESH_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [reload]);

    return (
        <div className="px-10">
            <Title>Dashboard</Title>
            {/* The mockup shows a settings gear next to the title — hidden
                for MVP. TODO(post-MVP): wire dashboard settings */}

            {dashboard ? (
                <div className={GRID_CLASSES}>
                    <KpiTile
                        label="Orders Placed"
                        count={dashboard.ordersPlaced.count}
                        deltaPct={dashboard.ordersPlaced.deltaPct}
                        positiveIsGood={true}
                    />
                    <KpiTile
                        label="Orders Shipped"
                        count={dashboard.ordersShipped.count}
                        deltaPct={dashboard.ordersShipped.deltaPct}
                        positiveIsGood={true}
                    />
                    <KpiTile
                        label="Items Low Stock"
                        count={dashboard.itemsLowStock.count}
                        deltaPct={dashboard.itemsLowStock.deltaPct}
                        positiveIsGood={false}
                    />
                    <IssuesTooltip breakdown={dashboard.issuesFound.breakdown}>
                        <KpiTile
                            label="Issues Found"
                            count={dashboard.issuesFound.count}
                            deltaPct={dashboard.issuesFound.deltaPct}
                            positiveIsGood={false}
                        />
                    </IssuesTooltip>
                </div>
            ) : readStatus === "failed" ? (
                <ErrorState onRetry={reload} />
            ) : (
                // Initial fetch only: once data exists the tiles stay mounted
                // through background refreshes, so the skeleton never flashes.
                <div className={GRID_CLASSES}>
                    {[0, 1, 2, 3].map((index) => (
                        <Skeleton
                            key={index}
                            shape="circle"
                            className="aspect-square w-full"
                            data-testid="dashboard-skeleton"
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
